/**
 * Wire contract between @acs/api and @acs/web.
 *
 * Design rule: the API sends *display-ready* data. The browser must never
 * parse a JSON-in-a-string column, recompute a live state, or hold a
 * secret. All of that happens server-side once, not once per row per render.
 */

import type {
  CustomerAssessmentStatus,
  CustomerSegment,
  CustomerStatus,
  LiveState,
  LogCategory,
  LogLevel,
  LogStatus,
  Mode,
  Province,
  ScheduleType,
  SessionStatus,
  ShiftFilter,
  ShiftPriority,
  WorkHours,
} from './domain.js'

/* ------------------------------------------------------------------ *
 * Pagination
 * ------------------------------------------------------------------ */

export interface Page<T> {
  rows: T[]
  /** Total matching the current filter. `null` when the count was skipped. */
  total: number | null
  limit: number
  offset: number
  /** Server-side query duration, surfaced in the UI's perf readout. */
  tookMs: number
}

/* ------------------------------------------------------------------ *
 * Customer row (the Approve/Pending/Confirm table)
 * ------------------------------------------------------------------ */

/**
 * One row of the customer table.
 *
 * Deliberately excludes `pin`, `sin_number`, `work_auth`,
 * `gmail_imap_app_password` and every other credential on the source
 * document. Those are never projected out of Mongo, so they cannot leak
 * through this type.
 */
export interface CustomerRow {
  /** `customers._id` — stringified numeric id. Stable React key. */
  id: string
  /** `customers.customer_id`, e.g. "mdesai000242". */
  customerId: string
  name: string
  email: string
  phone: string | null

  /** Parsed server-side from the `city_tags` JSON string column. */
  cities: string[]
  /** Parsed server-side from the `work_hours` JSON string column. */
  workHours: string[]
  /** Flattened count from `city_warehouses`; the detail panel shows the map. */
  warehouseCount: number

  mode: Mode
  status: CustomerStatus

  agentId: number | null
  agentUsername: string | null
  agentName: string | null

  /** Bare suffix as stored, e.g. "0000000554". */
  jobId: string | null
  /** Fully-qualified form for display/linking, e.g. "JOB-CA-0000000554". */
  jobIdFull: string | null
  scheduleId: string | null
  scheduleIdFull: string | null
  /**
   * Whether this customer's shift can be addressed in UUID form, which is what
   * the Brazil flow's create needs.
   *
   *   'resolved'  both UUIDs are on file for the pair this row currently carries
   *   'job_only'  the shift is known but its UUID has not been filled in yet
   *   'unknown'   no UUID on file for this pair
   *   null        no job and shift assigned. Not an error state.
   *
   * ONE derived enum rather than the two 40-character strings, because no
   * operator reads a UUID character by character and 828 rows would carry them
   * on every list and every change-stream frame. The raw values are on the
   * detail panel, which is one document per deliberate click.
   *
   * Derived from the cache AND the row's current pair, never from the cache
   * alone: the sibling system re-points `job_id` / `schedule_id` without
   * touching the cache, so a cache whose key no longer matches reads 'unknown'.
   * That is the truth, and it is what the run will re-resolve anyway.
   */
  targetUuid: 'resolved' | 'job_only' | 'unknown' | null

  /** `computer_number` — the "N S1" badge in the legacy panel. */
  computerNumber: string | null

  /**
   * Pollers this customer's hunt arms with, or `null` to use the hunt setting.
   *
   * `null` is the ordinary state and the one every customer starts in — the
   * Workers column renders the global number as a placeholder for it rather
   * than writing that number onto the row. Only an operator's explicit override
   * is ever a number here, which is what makes "reset to default" expressible.
   */
  huntWorkers: number | null

  /**
   * Shift-list (GraphQL) pollers, or `null` to use the hunt setting.
   *
   * The second detector: instead of asking one schedule for its status, these
   * ask the JOB which schedules it is advertising and match this customer's id
   * against the answer. Both fleets run at once and either can claim.
   *
   * Same `null`-means-inherit rule as `huntWorkers`, and the same reason for it.
   * `0` is a real value in both columns and means opposite things in each: zero
   * here runs the schedule watch alone (the default), zero there runs the shift
   * list alone.
   */
  huntGqlWorkers: number | null

  /**
   * GA workers, or `null` to use the hunt setting.
   *
   * The fourth fleet: it reads `get-all-schedules` for the customer's job. On
   * the Current flow it arms only while the assigned shift has no UUID yet, as
   * spares beside the schedule watch, to read the UUID off the job's listing —
   * and stands down once it lands. It never claims.
   *
   * ── Unlike the three above, `0` is NOT accepted ─────────────────────
   * Those are three ways of noticing the same opening, so turning one off is a
   * real sentence about which detectors a customer runs. This is the reader
   * that finds a shift's UUID, and a run whose shift has none needs at least
   * one — so `null` inherits and the floor is 1.
   *
   * It used to run BEFORE an application existed, waiting for the job to
   * advertise a shift so one could be created. The create now goes out at
   * Start and is retried if refused, with no worker armed until it takes.
   *
   * Authenticated, like the watch and the job record, so each worker holds a ring
   * of borrowed session accounts on top of its own ISP row.
   */
  scheduleWorkers: number | null

  /**
   * Which claim sequence this customer's run takes, or `null` to inherit.
   *
   * `'current'` fires one lean confirm the instant a detector sees the shift
   * open. `'brazil'` waits out the early-warning window and then works the
   * `v1/workflow` endpoint in a bounded loop — an endpoint on which creating the
   * application and being granted the seat are the same call.
   *
   * Same `null`-means-inherit rule as the worker counts above, and the same
   * "read once at Start" rule: a customer switched mid-run keeps the flow they
   * armed with and picks the new one up on their next Start.
   *
   * The one field on this row that changes what START does rather than how fast
   * the claim is. A Brazil customer files no application until a shift opens, so
   * their run looks quieter at the beginning than a current-flow one — by design.
   */
  huntFlow: 'current' | 'brazil' | null

  /**
   * The account PIN, in the clear — on exactly two surfaces.
   *
   * `listCustomers` projects it for the two working lists (Pending and
   * Approved, the screens where an operator checks a handover against what was
   * typed in), and `getCustomerDetail` projects it for the side panel at every
   * status — one document, per explicit operator click. On every other query it
   * is `null` by construction: absent from the SSE stream, from search, from
   * the Confirmed list and from every log line.
   *
   * Each surface is a deliberate reversal of "credentials never reach a DTO",
   * taken on purpose and documented at its projection (`PIN_STATUSES` and
   * `DETAIL_PROJECTION` in the repo). Widening it further — to Confirmed, to a
   * search result — needs the same decision taken again.
   */
  pin: string | null

  /**
   * What Amazon last said about hiring this person, or `null` if never asked.
   *
   * Written by the rehire gate at sign-in, which stops the session outright when
   * `blocked`. Three states, and the third one matters: `null` means the gate has
   * never run for this customer, and the column must not paint that like a
   * cleared verdict.
   *
   * Carries no credential and no protected characteristic. The endpoint it comes
   * from returns date of birth, phone numbers, a national id, gender, ethnicity,
   * disability and veteran status, and criminal-record fields; the gate reads
   * four scalars out of it by name and lets the rest go. Widening this — here, or
   * in `rehire-store.ts` — is a decision to take again on purpose.
   */
  rehireEligibility: {
    /**
     * Amazon's own word, verbatim, e.g. `Ineligible for Rehire - Within Waiting
     * Period`. Never mapped to a local vocabulary — the operator reads it.
     *
     * `''` is a real value and means eligible: it is what an account with no
     * employment record returns.
     */
    status: string
    blocked: boolean
    /** ISO instant Amazon says they become eligible, when it supplied one. */
    futureEligibleAt: string | null
    checkedAt: string
  } | null

  /*
   * `sessionId`, `sessionStatus`, `currentStep`, `currentStepLabel`,
   * `stepIndex` and `lastHeartbeatAt` used to be here.
   *
   * They are denormalised onto `customers` by a SIBLING worker fleet that
   * shares this database, and they are no longer read — not projected, not
   * mapped, not displayed. Carrying them meant this console reported another
   * system's progress for a customer as if it were its own work.
   *
   * What this system is doing for a customer arrives on the run registry
   * instead, as `RunStateDto`.
   */

  /** This system's run, collapsed for the Session column. Never the sibling's. */
  liveState: LiveState

  /**
   * When this customer's shift was actually claimed, or `null` if it never was.
   *
   * `customers.won_at`, the same indexed instant the Won list filters and sorts
   * on — not a second copy of it and not a derived flag. It is on the ROW rather
   * than only in the detail panel because a claim is the one thing that happens
   * to a customer without anybody watching: the run stands down, the status flips
   * to `confirmed`, and the row leaves the Sessions list in the same breath. By
   * the time an operator looks, the only surviving evidence is this field.
   *
   * `null` is an ordinary value, not a gap. A customer moved to Confirmed by
   * hand never won anything, and the row must not paint them as though they had
   * — see the note in `customers.write.ts` on why no move fabricates a `won_at`.
   *
   * Costs one date on the wire for every row of every list. Weighed against
   * widening `ROW_PROJECTION`: it carries no credential, no protected
   * characteristic and nothing the run log does not already hold in full.
   */
  wonAt: string | null

  /**
   * `customers.gender` — `'Male'`, `'Female'`, or `null` when nobody has set it.
   *
   * On the ROW, not only in the detail panel, because Start needs it before the
   * request leaves the browser. The audio assessment speaks in a voice chosen by
   * this field and has no default: a customer without one runs the whole way to
   * a won shift and then stops dead at the recorded questions, hours after the
   * operator pressed the button. So `POST /start` refuses a genderless customer
   * with `gender_required`, and the table asks the operator to set it FIRST — for
   * every ticked row that lacks one, in one dialog, before any of them start.
   *
   * Reading it off the row is what makes that gate free: no per-customer lookup
   * at click time, no round trip for the fifty rows that already have one.
   *
   * `null` is the ordinary value today. Most rows on file predate the field.
   * The only two strings that ever appear are the two the voice lookup knows;
   * anything else in the document reads as `null` here rather than as a third
   * gender the assessment would refuse.
   *
   * Widens `ROW_PROJECTION` by a closed two-value enum that the sibling system
   * writes to the same document. It is a protected characteristic in the
   * general sense, and it is already on the detail panel and the edit form; it
   * appears on the row so the gate can read it, and the row renders nothing
   * from it.
   */
  gender: Gender | null

  /**
   * `customers.assessmentStatus` — how far the most recent audio assessment
   * got, or `null` for a customer who has never sat one.
   *
   * On the ROW rather than only in the detail, for the same reason `wonAt` is:
   * an assessment finishes with nobody watching, minutes into a run, and the
   * drawer is the place an operator then hands the questions back to the
   * customer. Carrying the flag on the row means the change-stream frame for
   * the run's own final write lights the drawer's action the moment it lands,
   * with no polling and no second request — and the questions themselves,
   * which run to kilobytes, stay behind `GET /customers/:id/assessment` until
   * somebody actually opens them.
   *
   * A closed four-value enum, no credential; anything else in the document
   * reads as `null` rather than as a fifth state nothing renders.
   */
  assessmentStatus: CustomerAssessmentStatus | null

  /**
   * `customers.shift_priority` — `'Any'`, `'Day Shift'`, `'Night Shift'`, or
   * `null` when the row predates the column.
   *
   * On the ROW, because every list draws it: a sun, a moon, or both, beside
   * the customer id — the one cell present on every list at every width. One
   * short closed enum per row, no credential; anything else in the document
   * reads as `null` here rather than as a fourth priority nothing renders.
   *
   * Edited in place from the drawer's Contact section, optimistically, and
   * shipped by the change stream (`CUSTOMER_FIELDS` in watchers.ts) so the
   * icon one console shows is never a save behind another's.
   */
  shiftPriority: ShiftPriority | null

  createdAt: string
  updatedAt: string
}

/* ------------------------------------------------------------------ *
 * Customer detail (side panel)
 * ------------------------------------------------------------------ */

/**
 * The two voice pools the audio assessment draws from.
 *
 * Capitalised because that is what the sibling system writes into this same
 * field on this same document — one operator setting it in either console has
 * to be enough, and normalising on one side would make the two disagree.
 */
export const GENDERS = ['Male', 'Female'] as const
export type Gender = (typeof GENDERS)[number]

export interface CustomerDetail extends CustomerRow {
  /**
   * The raw UUID form of this customer's job and shift, exactly as stored.
   *
   * On the DETAIL only, and that is the trade `CustomerRow.targetUuid` exists to
   * make: two 40-character strings are worth carrying for one document behind a
   * deliberate click, and not worth carrying on 828 rows nobody reads them on.
   *
   * Null when nothing is cached, when the cache is stale — see `targetUuid` for
   * what makes it stale — or when the value itself has not been resolved.
   */
  jobUuid: string | null
  scheduleUuid: string | null
  addressLine1: string | null
  addressLine2: string | null
  addressCity: string | null
  addressState: string | null
  addressZipcode: string | null
  /**
   * `YYYY-MM-DD`, exactly as the collection holds it — never a timestamp.
   *
   * A value of any other shape is a record written by something other than this
   * console, and it is carried here UNTOUCHED so an operator can see what is
   * actually stored. Clients must render that case verbatim rather than parsing
   * it: a date-only string parses as midnight UTC, and formatting *that* in a
   * browser west of Greenwich prints the previous day. Use `parseDob` /
   * `formatDob` from this package, which cannot make that mistake.
   */
  dateOfBirth: string | null
  /**
   * `'Male' | 'Female' | null` — which voice the audio assessment speaks in.
   *
   * Operator-entered, and required before a customer can sit one: there is
   * deliberately no default voice, because a recording that does not sound like
   * the applicant is worse for them than a run that stopped and said why.
   *
   * On the DETAIL only, like the address and the date of birth. It is a
   * protected characteristic and belongs on the one screen where a person is
   * checking a record against what was typed in, not on a list, not on the SSE
   * stream and not in any log line.
   */
  gender: Gender | null
  isPrimary: boolean
  notes: string | null
  /**
   * The SIN, in the clear — the second deliberate reversal of "credentials
   * never reach a DTO", after `pin` on the working lists (and, since 2026-09,
   * on this detail too — see `CustomerRow.pin`). The detail panel is
   * where an operator checks a handover against what was typed in, and a
   * value they cannot read is a value they stop checking. Narrowed to this
   * one DTO: it stays out of `CustomerRow`, the SSE stream and every log
   * line, and widening it further is a decision to take again on purpose.
   */
  sinNumber: string | null
  /** Parsed `city_warehouses`: city → site ids. */
  cityWarehouses: Record<string, string[]>
  /** Redacted presence flags — never the values themselves. */
  has: {
    pin: boolean
    sinNumber: boolean
    workAuth: boolean
    imapAppPassword: boolean
  }
  /**
   * Every shift this customer has had fully secured, oldest first.
   *
   * A HISTORY, and the distinction from `wonAt` beside it is the whole reason
   * this exists. `wonAt` is the latest CLAIM — a soft seat held for a few hours,
   * with the application still to file — and each new claim overwrites it. These
   * are the far rarer moments where the whole form went in behind the claim and
   * Amazon closed the workflow, and they accumulate: a customer run three times
   * carries three entries.
   *
   * On the DETAIL only, for the same reason the address and the UUIDs are: it is
   * bounded but not free (the writer caps it at twenty), it is read behind a
   * deliberate click, and it belongs nowhere near a list of 828 rows. It is also
   * absent from `CustomerRow` on purpose — the realtime stream patches rows field
   * by field, so a field the row DTO does not carry is one a patch can never
   * blank on an open drawer.
   *
   * EMPTY is the ordinary state and means this customer has never carried an
   * application all the way through — which is true of everyone who has only
   * ever claimed.
   */
  securedShifts: SecuredShiftDto[]
}

/**
 * One fully secured shift, as the wizard recorded it at `supplementary-success`.
 *
 * Written by the API's `hiring/onboarding/hard-secured-store.ts` and read
 * nowhere else. Every field is exactly what Amazon named at the time, so an
 * entry stays true about the moment it describes even after the customer is
 * re-pointed at a different job.
 */
export interface SecuredShiftDto {
  /** The application Amazon accepted. The handle a person quotes. */
  applicationId: string
  /** Bare suffix, e.g. `"0000000554"`. */
  jobId: string | null
  /** Qualified for display, e.g. `"JOB-CA-0000000554"`. */
  jobIdFull: string | null
  scheduleId: string | null
  scheduleIdFull: string | null
  /** e.g. `"Everett"`. Null when the schedule body named none. */
  city: string | null
  /** e.g. `"WA"`. The half that makes `city` unambiguous. */
  state: string | null
  /** QUALIFIED, e.g. `"SITE-SWA1"` — not the bare location code. */
  siteId: string | null
  /** e.g. `"PART_TIME"`. NOT the employment type. */
  scheduleType: string | null
  /**
   * `"Tuesday, Sep 03"` — the site's own rendering, with NO YEAR.
   *
   * Null across all three appointment fields means the run submitted the
   * application without booking an orientation, which is a real outcome and not
   * a missing value. Render it as such rather than hiding the entry.
   */
  appointmentDate: string | null
  /** `"10:00 AM"`, normalised. */
  appointmentTime: string | null
  /**
   * `DD/MM/YYYY`, verbatim — the only half that carries a year.
   *
   * Never parse this with `new Date()`: it is day-first, so every day of a month
   * up to the 12th parses "successfully" as the wrong date.
   */
  appointmentDateRaw: string | null
  /** ISO 8601. When `supplementary-success` arrived — what the entry is about. */
  securedAt: string
  /** The run that produced this entry, for joining against the run log. */
  runId: string | null
}

/* ------------------------------------------------------------------ *
 * The audio assessment record
 * ------------------------------------------------------------------ */

/** One spoken question, as it was put to the candidate. */
export interface CustomerAssessmentQuestionDto {
  order: number
  /** Amazon's module name, e.g. `Tier1_LPaCA_BandC_Delivery`. */
  module: string
  kind: 'soundcheck' | 'evaluation'
  /** e.g. "Part 2 - What would you do", when the question has one. */
  partLabel: string | null
  /** The task prompt, HTML stripped. */
  questionText: string
  /** The clip the candidate listened to, e.g. "S25_M1_Main". */
  clipLabel: string | null
  /**
   * The transcript of that clip — the story the question is ABOUT.
   *
   * Part of the question, not of the answer. A record that said only
   * "summarise the situation" would be a record of nothing.
   */
  situationText: string | null
  maxRecordingSec: number
  askedAt: string | null
}

/**
 * A customer's most recent audio assessment. **Questions only.**
 *
 * Nothing about what was said is here, deliberately: this record exists so a
 * customer can be handed a document of what they were asked, and the answers are
 * not theirs in the way the questions are. `workHistoryEmployer` is the one
 * answer carried, because it was picked for them.
 *
 * Always the LATEST attempt — a new run overwrites every field — so this is a
 * snapshot, not a history.
 */
export interface CustomerAssessmentDto {
  customerId: string
  startedAt: string | null
  endedAt: string | null
  status: CustomerAssessmentStatus | null
  /** Why it ended, when it did not complete. The engine's own words. */
  endReason: string | null
  workflowInstanceId: string | null
  modulesCompleted: string[]
  questions: CustomerAssessmentQuestionDto[]
  /**
   * The employer the work-history questionnaire named — picked at random once
   * per customer and kept for every later assessment, so it is not something
   * the customer knows unless told. `null` when none has been saved.
   */
  workHistoryEmployer: string | null
}

/* ------------------------------------------------------------------ *
 * "Get OTP" — the verification code already sitting in a customer's inbox
 * ------------------------------------------------------------------ */

/**
 * Why a code read came back without a code.
 *
 * `none` is the ORDINARY outcome — nothing recent in the mailbox — and not a
 * failure of the read. The other four are: the app password was refused, the
 * mailbox could not be reached, the operator navigated away mid-read, and
 * anything unclassified.
 */
export type OtpFetchReason = 'none' | 'imap_auth' | 'imap_connect' | 'aborted' | 'internal'

/**
 * The newest Amazon verification code already sitting in the customer's Gmail.
 *
 * READ-ONLY AND SIDE-EFFECT FREE, and that is the whole difference between this
 * and a sign-in: nothing is sent to Amazon, no new code is triggered, and the
 * mailbox is opened `readOnly` so nothing is flipped to \Seen. Pressing the
 * button again is therefore free — which is exactly what an operator on a phone
 * call with a customer needs it to be.
 *
 * `ok: false` is a 200, not an error status: "no code has arrived yet" is an
 * answer, and the commonest one. Only a bad request (unknown customer, no app
 * password on file, a read already running) is a non-2xx.
 *
 * The code is returned for display and never stored, never logged, and never
 * written back to the customer document.
 */
export interface OtpFetchResponse {
  /** True when a code was read; `code` is non-null exactly then. */
  ok: boolean
  /** The six digits, or null. Never logged server-side. */
  code: string | null
  /** When the mail carrying it arrived (IMAP INTERNALDATE), ISO. */
  receivedAt: string | null
  /**
   * Its age at read time, as the SERVER measured it.
   *
   * The client must render this rather than computing an age from `receivedAt`
   * against its own clock: only the server saw both its own clock and the mail
   * server's, and an operator's laptop running two minutes fast would otherwise
   * paint a code that just landed as one that has expired.
   */
  ageMs: number | null
  /**
   * Which folder it was found in — `INBOX`, or the junk folder's real path.
   *
   * Worth a line on screen: a code repeatedly found in Spam is a deliverability
   * problem to fix at the mailbox, not a flake to keep absorbing.
   */
  mailbox: string | null
  /** How long the read took end to end, ms. Shown when it fails. */
  tookMs: number
  /** The freshness window used, ms — so the UI's "in the last N minutes" agrees
   *  with the server's actual rule rather than restating a guess. */
  windowMs: number
  reason?: OtpFetchReason
  /** Operator-safe sentence, safe to show verbatim. Never carries a secret. */
  message: string
  /**
   * What was actually in the mailbox when no code was found.
   *
   * Only present on a `none` outcome, and only when the census could be taken.
   * It is the difference between "Amazon has not sent one" and "one was sent an
   * hour ago and this window is doing its job" — two answers that look identical
   * on screen and need two entirely different next moves.
   */
  probe?: {
    /** Messages from the expected sender in the probe window. */
    fromSender: number
    /** Age of the newest of those, ms. Null when there were none. */
    newestAgeMs: number | null
    /** How far back the census looked, ms. */
    windowMs: number
  }
}

/* ------------------------------------------------------------------ *
 * "Login link" — hand a human the session this system already holds
 * ------------------------------------------------------------------ */

/**
 * Why a login link could not be built.
 *
 * `none` is the ORDINARY outcome — this customer has no usable session on file,
 * which is simply what "has not been started recently" looks like. The other
 * three are: the console is pointed at a market this link cannot serve, the row
 * carries no email to key a session by, and anything unclassified.
 */
export type LoginLinkReason = 'none' | 'wrong_market' | 'no_email' | 'internal'

/**
 * A one-click sign-in for the session this system is already holding.
 *
 * ── This is the THIRD deliberate reversal of "credentials never reach a DTO" ──
 * The first two are `pin` on `CustomerRow` and `sinNumber` on `CustomerDetail`,
 * each documented where it sits. This one is the widest of the three and needs
 * reading as a decision rather than an oversight, because `SignInResultDto`
 * three hundred lines below says the exact opposite for the exact same tokens —
 * "the captured tokens are reported only as booleans, since the browser has no
 * reason to hold them and every reason not to" — and that remains true THERE.
 *
 * The difference is the question being answered. `SignInResultDto` describes a
 * run to anyone watching one; this answers "let a human finish this by hand",
 * which cannot be answered without handing over the session. The alternative is
 * a second sign-in, which emails the customer another verification code and
 * spends another attempt against a rate-limited service — so the choice is not
 * between disclosing and not disclosing, it is between disclosing to the
 * operator who asked and making the customer pay for a duplicate.
 *
 * What keeps it narrow, and what must stay true of any change here:
 *   · it is reachable only from its own endpoint, only on an explicit operator
 *     action, and only for one customer at a time — never from `RunStateDto`,
 *     the SSE run map or `/run-log`, which are broadcast continuously;
 *   · `url` is never logged, on either side;
 *   · the browser holds it on one component instance, not in a query cache.
 *
 * `ok: false` is a 200, not an error status: "there is no live session for this
 * customer" is an answer, and the commonest one. Only an unknown customer or a
 * row with no email is a non-2xx.
 */
export interface LoginLinkResponse {
  /** True when a link was built; `url` is non-null exactly then. */
  ok: boolean
  /**
   * The full `#/auth-return` URL.
   *
   * A BEARER CREDENTIAL. Whoever holds this can act as the customer until the
   * session expires. Never log it, never persist it, never put it in a URL bar
   * that is not the one being navigated.
   */
  url: string | null
  /** When the session behind it stops working, ISO. Null when there is none. */
  expiresAt: string | null
  /**
   * Its remaining life as the SERVER measured it.
   *
   * The client must render this rather than differencing `expiresAt` against its
   * own clock — the same rule, for the same reason, as `OtpFetchResponse.ageMs`.
   */
  expiresInMs: number | null
  /**
   * The login the session belongs to, so an operator can confirm the link is for
   * who they think before sending it to somebody. Not a secret — `RunStateDto`
   * already carries it.
   */
  email: string | null
  reason?: LoginLinkReason
  /** Operator-safe sentence, safe to show verbatim. Never carries a secret. */
  message: string
}

/* ------------------------------------------------------------------ *
 * Session
 * ------------------------------------------------------------------ */

export interface SessionSummary {
  sessionId: string
  customerId: string
  customerObjectId: string
  email: string
  status: SessionStatus
  provider: string
  currentStep: string | null
  currentStepLabel: string | null
  stepIndex: number
  liveState: LiveState
  retryCount: number
  workerId: string | null
  replicaId: string | null
  lastError: string | null
  createdAt: string
  startedAt: string | null
  updatedAt: string
  lastHeartbeatAt: string | null
  /** `customer_sessions.metadata` */
  applicationId: string | null
  jobId: string | null
  scheduleId: string | null
  lastScheduleState: string | null
}

/* ------------------------------------------------------------------ *
 * Logs
 * ------------------------------------------------------------------ */

export interface LogEntry {
  id: string
  logId: string
  sessionId: string
  customerId: string | null
  level: LogLevel
  category: LogCategory | null
  stepKey: string | null
  stepLabel: string | null
  status: LogStatus | null
  message: string
  smallDetail: string | null
  attempt: number | null
  maxAttempts: number | null
  createdAt: string
}

/* ------------------------------------------------------------------ *
 * Session stats — the strip above the live log
 * ------------------------------------------------------------------ */

/**
 * At-a-glance health of one session.
 *
 * Every figure is derived from *structured* fields (`level`, `status`,
 * `stepKey`) rather than by parsing message text. The legacy panel counted HTTP
 * status codes by reading strings out of log lines, which breaks the moment
 * anyone rewords a message. Counting `stepKey === 'waf_recovery'` cannot.
 */
export interface SessionStats {
  sessionId: string
  /** Total log lines retained for this session (TTL keeps ~30 days). */
  events: number
  byLevel: Record<LogLevel, number>
  /** Successful `keep_alive_ping` steps — the session's heartbeat count. */
  keepAlives: number
  /** `waf_recovery` occurrences. Historically ~1 per keep-alive. */
  wafChallenges: number
  /** Log lines with status `retrying`. */
  retries: number
  /** Log lines with level `error`. */
  errors: number
  /** ISO of the first and most recent log line, for the uptime readout. */
  firstEventAt: string | null
  lastEventAt: string | null
  /** Milliseconds between the session starting and its latest event. */
  activeMs: number | null
  /** Mean gap between consecutive keep-alives, in ms. `null` under 2 samples. */
  meanKeepAliveGapMs: number | null
  tookMs: number
}

/* ------------------------------------------------------------------ *
 * Schedule
 * ------------------------------------------------------------------ */

export interface ScheduleRow {
  scheduleId: string
  jobId: string
  /**
   * The UUID form of `jobId`, verbatim: `JOB-CA-dcae9b5d-3382-…`.
   *
   * Mixed case on purpose — upper qualifier, lower suffix — because that is
   * exactly how Amazon issued it and how a request body has to carry it.
   */
  jobUuid: string | null
  /**
   * The UUID form of `scheduleId`. Null on most rows, and that is ordinary:
   * the sibling poller resolves these asynchronously.
   */
  scheduleUuid: string | null
  /** When the poller resolved it, so the operator can read the age. */
  scheduleUuidAtMs: number | null
  /** Non-null means the poller gave up — no UUID is coming for this shift. */
  scheduleUuidGaveUpAtMs: number | null
  city: string
  state: string
  siteId: string
  address: string
  alpsCode: string
  scheduleText: string
  scheduleType: ScheduleType
  scheduleApiStatus: string
  status: string
  hireStartDate: string | null
  hireEndDate: string | null
  /** The four counts the operator watches — soft match is the money number. */
  laborDemandAvailableCount: number
  laborDemandHardMatchCount: number
  laborDemandSoftMatchCount: number
  laborDemandOpenCount: number
  updatedAt: string
  updatedAtMs: number
}

/* ------------------------------------------------------------------ *
 * Aggregates for the header strip
 * ------------------------------------------------------------------ */

export interface ConsoleStats {
  customers: Record<CustomerStatus, number>
  /**
   * The segmented queues' slice of `customers` above, by status.
   *
   * NOT subtracted from it: `customers` still counts every customer, and this
   * says how many of them belong to a queue that has its own rail entry. The
   * rail and the topbar do the subtraction once, in `customerCounts()`.
   *
   * Both numbers come from one `$facet` over one stream, so
   * `customers.pending - segments.telegram.pending` is exact rather than two
   * readings of a moving collection that can fail to agree.
   */
  segments: Record<CustomerSegment, Record<CustomerStatus, number>>
  live: Record<LiveState, number>
  /**
   * Customers whose shift was actually CLAIMED — `won_at` is set.
   *
   * Not a live state and deliberately not in `live` above: those describe what a
   * run is doing right now and are erased when it ends, while this is a durable
   * outcome that survives the run, the process and the redeploy. A customer can
   * be won and never_started at the same time, and both are true.
   *
   * Counts the claim, never the sighting. A worker that saw ACTIVE and lost the
   * race leaves this number alone and keeps hunting — see `hiring/won.ts`.
   */
  won: number
  /**
   * Customers carrying an unclaimed sighting — `active_hit_at` is set.
   *
   * Never overlaps `won`: the claim clears this field in the same write that
   * sets `won_at`, so the two counts partition the customers who have seen a
   * shift go ACTIVE rather than double-counting the ones who eventually got one.
   */
  activeHits: number
  /** Sessions whose heartbeat has gone quiet while still marked running. */
  staleCount: number
  agents: { username: string; name: string | null; count: number }[]
  modes: Record<Mode, number>
  generatedAt: string
  tookMs: number
}

/* ------------------------------------------------------------------ *
 * Creating a customer
 * ------------------------------------------------------------------ */

/**
 * One city and the warehouses that hire for it.
 *
 * Built from `hiring_job_details`, which is the only place the pairing is
 * recorded: one document per site, carrying `site_id` ("SITE-DCG4") and
 * `location_name` ("Calgary, AB"). The `cities` collection is NOT this — it
 * holds two rows of test data ("Torronto" → ["pqr","stu"]) and nothing reads it.
 */
export interface CityOption {
  /** Lower-cased, exactly as `city_tags` stores it: "calgary, ab". */
  key: string
  /** As `hiring_job_details.location_name` spells it: "Calgary, AB". */
  label: string
  /** Every `SITE-…` that hires here, sorted. */
  sites: string[]
}

/**
 * What the create form sends.
 *
 * Deliberately NOT `Partial<CustomerRow>`. A row is what the table reads back —
 * derived state, formatted ids, presence booleans. This is the far smaller set
 * of things a human types, and every id, counter, timestamp and default is the
 * server's to decide.
 */
export interface CustomerDraft {
  name: string
  email: string
  /** Ten digits, however the operator punctuated them. */
  phone: string
  /** Write-only. Never returned by any endpoint. Six digits. */
  pin: string
  /**
   * Write-only. The Gmail app password used to read the OTP over IMAP.
   * Sixteen characters — the server strips the spaces Google displays it with.
   */
  imapAppPassword: string

  /** Keys from `CityOption.key`. At least one. */
  cities: string[]
  /**
   * city key → the chosen `SITE-…` codes, listed explicitly.
   *
   * An empty list is read by the server as "every site in that city", which is
   * a lenient default for other callers — the form never relies on it and
   * always sends the full set, because a stored `city_warehouses` in this
   * collection always enumerates its sites.
   */
  warehouses: Record<string, string[]>
  /** At least one. */
  workHours: WorkHours[]
  /**
   * Required, and a closed enum: the sibling agent panel writes the same
   * column with the same three strings, and a fourth spelling would be a value
   * that panel's own form cannot round-trip. Only meaningful alongside
   * `Full-time` hours, but always asked for — see `SHIFT_PRIORITIES`.
   */
  shiftPriority: ShiftPriority

  /**
   * `agents.username`, or null for a customer numbered under `admin`.
   *
   * Null is a real state in the collection — 166 customers are in it — so the
   * wire contract keeps it. The create screen requires a choice.
   */
  agentUsername: string | null
  /** Optional. */
  notes: string | null

  /** Write-only. Nine digits. */
  sinNumber: string
  /** `YYYY-MM-DD`, as the collection stores it. */
  dateOfBirth: string
  /**
   * Optional at creation, required before the audio assessment can run.
   *
   * Left optional here so nothing about adding a customer changes: the
   * assessment refuses with a message naming the field and the customer, which
   * is a better place to learn it than a create form nobody could submit.
   */
  gender?: Gender | null

  addressLine1: string
  /** Apartment or unit — optional. */
  addressLine2: string | null
  addressCity: string
  addressProvince: Province
  addressPostalCode: string

  mode: Mode
}

/** What the server allocated, so the UI can name the row it just made. */
export interface CustomerCreated {
  /** `customers._id` / `id`, as a string. */
  id: string
  /** The business key the sequence produced, e.g. "yunita000085". */
  customerId: string
  name: string
  email: string
  status: CustomerStatus
  createdAt: string
}

/* ------------------------------------------------------------------ *
 * "Fill from message" — paste what the customer sent, the form fills itself
 * ------------------------------------------------------------------ */

/** Body of `POST /api/customers/autofill`: the raw text an operator pasted. */
export interface AutofillRequest {
  text: string
}

/**
 * Hard cap on the pasted text, enforced on both sides.
 *
 * Generous for a WhatsApp message (a long one is a few hundred characters)
 * and still a bounded, cheap model call: the prompt is fixed, so this is the
 * only part of the request whose cost the client controls.
 */
export const AUTOFILL_MAX_CHARS = 8_000

/**
 * The form fields the text contained, each already normalised to what the
 * create form accepts — an ISO date, the full province name, a canonical
 * postal code, a ten-digit phone. A key is present only when the text
 * actually stated that value: nothing here is guessed or defaulted, which is
 * what lets the client paint every key it receives without a second look.
 *
 * Deliberately absent, by decision: `cities` and `warehouses` (a wrong site is
 * a customer sent to the wrong building, so the operator picks them by hand
 * every time), `agentUsername` (bookkeeping, not something a customer says),
 * `mode` and `notes` (leftover prose would be noise in front of whoever runs
 * the customer).
 *
 * Two values are passed through even when the form will refuse them, because
 * showing what the message said next to the form's own error beats a blank:
 * a PIN of the wrong length (4–8 digits) and a SIN of nine digits whatever its
 * checksum. Everything else that fails a rule lands in `skipped` instead.
 */
export interface AutofillFields {
  name?: string
  /** `647-555-0142` — ten digits, dashed. */
  phone?: string
  email?: string
  pin?: string
  /** `abcd efgh ijkl mnop` — sixteen letters, spaced as Google shows them. */
  imapAppPassword?: string
  workHours?: WorkHours[]
  shiftPriority?: ShiftPriority
  sinNumber?: string
  /** `YYYY-MM-DD`, a real calendar date in the past. */
  dateOfBirth?: string
  gender?: Gender
  addressLine1?: string
  addressLine2?: string
  addressCity?: string
  addressProvince?: Province
  /** Canonical `M1M 2Z9`. */
  addressPostalCode?: string
}

/**
 * A value the text carried that the server refused to pass on — read as
 * something, but not well-formed enough to be that field (a phone with nine
 * digits, a date that is not a real date). Surfaced so the operator knows the
 * message HAD it and can type it by hand, rather than wondering why a field
 * stayed blank.
 */
export interface AutofillSkipped {
  field: keyof AutofillFields
  /** Operator-safe sentence, safe to show verbatim. Never a secret. */
  reason: string
}

export interface AutofillResult {
  fields: AutofillFields
  skipped: AutofillSkipped[]
  /** Wall-clock of the whole read, model call included. */
  tookMs: number
}

/* ------------------------------------------------------------------ *
 * Server features — what this deployment can do
 * ------------------------------------------------------------------ */

/**
 * Capabilities that depend on how the API was DEPLOYED rather than on who is
 * signed in. Carried on `/api/auth/me` because that is the one request the
 * console already makes before it renders anything, so a feature can be
 * drawn as absent — with the reason — instead of as a button that fails.
 *
 * Facts about configuration, never secrets: whether a key exists, not what
 * it is.
 */
export interface ServerFeatures {
  /** True when `OPENAI_API_KEY` is set, so "Fill from message" can run. */
  autofill: boolean
}

/** What to assume until `/api/auth/me` has answered: nothing optional is on. */
export const NO_FEATURES: Readonly<ServerFeatures> = Object.freeze({ autofill: false })

/** `GET /api/auth/me`. */
export interface AuthMeResponse {
  authenticated: boolean
  /** The operator username, or null when there is no session. */
  subject: string | null
  features: ServerFeatures
}

/* ------------------------------------------------------------------ *
 * Query params
 * ------------------------------------------------------------------ */

/**
 * The per-column filter params, as one list.
 *
 * This exists because the same seven names have to be written out in five
 * places — the URL parser, the URL serialiser, the wire query, the request
 * params, and the row of inputs — and the fifth one is always the one that gets
 * forgotten. It was: `normalizeQuery` in `use-customers` builds the request
 * params AND the cache key from an explicit key-by-key copy, so six of these
 * were dropped between the URL and the network. The URL updated, the table
 * refetched, and the server was never told what to filter by.
 *
 * `CustomerQuery` extends this list, so a name added here must exist on the
 * query, and every consumer that loops over it stays in step by construction.
 * Ordered the way the columns appear in the table.
 */
export const CUSTOMER_COLUMN_FILTERS = [
  'name',
  'agentName',
  'email',
  'job',
  'schedule',
  'city',
  'workHours',
  'computerNumber',
] as const
export type CustomerColumnFilter = (typeof CUSTOMER_COLUMN_FILTERS)[number]

export interface CustomerQuery extends Partial<Record<CustomerColumnFilter, string>> {
  status?: CustomerStatus
  mode?: Mode[]
  agent?: string[]
  /**
   * Agent usernames to EXCLUDE — the complement of `agent` above.
   *
   * Exists for the segmented queues: the Telegram list owns the `tg` agent's
   * pending customers, so the main Pending list has to be able to say "everyone
   * except them". Expressing that as `agent` with the other twelve usernames
   * listed would break the moment somebody is hired.
   *
   * Derived from the VIEW and deliberately never carried in the URL. That is
   * what makes a hand-typed `/customers/pending` exclude the queue too, rather
   * than only the link in the rail.
   *
   * ANDs with `agent` rather than overriding it, so asking for both a username
   * and its exclusion correctly returns nothing instead of silently picking one.
   */
  agentNot?: string[]
  liveState?: LiveState[]
  /**
   * Only customers whose shift priority is one of these — `day`, `night`,
   * `any`, or `unset` for a row that has none on file. A multi-select, like
   * `liveState`: picking `day` and `any` together lists everyone who would
   * take a day shift, which is the question a day-heavy site actually asks.
   *
   * Cheap: an equality on the document itself, applied as a residual on the
   * `status`-led index walk the list already does, so it costs nothing a
   * `mode` filter did not.
   */
  shift?: ShiftFilter[]
  /**
   * Only customers whose shift was CLAIMED.
   *
   * Deliberately a flag rather than a `LiveState` member. A live state describes
   * what a run is doing and is gone when the run ends; this is a durable outcome
   * that outlives it, so a won customer keeps whatever live state they have —
   * usually `never_started`, because winning is what stops the run. Folding the
   * two together would mean a customer stopped being "won" the moment the hunt
   * that won it was torn down.
   *
   * Cheap in a way the live-state filter cannot be: it narrows on an indexed
   * field on the document itself, so it needs no id list from the run registry
   * and no plan (see `planLiveStateFilter`).
   */
  won?: boolean
  /**
   * Only customers who saw a shift go ACTIVE and did not get it.
   *
   * The other outcome of the same race, and durable for the same reason: a
   * worker reading ACTIVE off the schedule is not a win, most sightings are lost
   * to somebody faster, and until this existed a customer who missed six
   * openings was indistinguishable from one that never saw anything.
   *
   * MUTUALLY EXCLUSIVE with `won`, and enforced by the write rather than the
   * query: claiming a shift `$unset`s `active_hit_at` in the same operation that
   * sets `won_at`, so a customer moves between the two lists atomically. That is
   * what lets this stay a single indexed scan — it asks whether `active_hit_at`
   * exists, never whether `won_at` does not, which no index can serve.
   */
  activeHit?: boolean
  /** OR-search across customerId / name / email / phone / job / schedule. */
  q?: string
  /** Exact job, after `JOB-CA-` is stripped. Not the header filter — see `job`. */
  jobId?: string
  /** Exact schedule, after `SCH-CA-` is stripped. */
  scheduleId?: string

  /* ---------------- per-column header filters ----------------
   *
   * One field per column of the table, each narrowing THAT column and nothing
   * else — the row of inputs under the header. They AND with each other, with
   * `q`, and with the facets above, so "agent: manthan" plus "city: calgary"
   * means both.
   *
   * Distinct from `q` on purpose: `q` is the toolbar's one box and ORs across
   * six columns, which is right for "find this person" and useless for "show me
   * every row whose *agent* contains man". The two are different questions and
   * a shared parameter could only answer one of them.
   * ---------------------------------------------------------- */

  /** Customer column: `name` or `customer_id`. */
  name?: string
  /** Email column. */
  email?: string
  /** Agent column: display name or username. */
  agentName?: string
  /**
   * Job column: the job id. Used to match the schedule id as well, back when
   * one stacked cell showed both; the Approved list now has a column — and a
   * filter — per id, so each matches its own field and nothing else.
   */
  job?: string
  /** Schedule column: the schedule id. */
  schedule?: string
  /** Cities column. Substring of the stored `city_tags` list. */
  city?: string
  /** Work hours column. */
  workHours?: string
  /** Status column: `computer_number`, the operator's own "N S2" tag. */
  computerNumber?: string

  /**
   * `won_at` exists so the Won list can sort on the same index it filters on.
   *
   * Without it that list is an IXSCAN over `won_at_-1` followed by a blocking
   * in-memory SORT on `created_at` — correct, and it throws away the fact that
   * the index is already in exactly the order the list wants. With it the query
   * is one index scan, already ordered, and the sort stage disappears.
   *
   * It is also what the CONFIRMED list opens on, without the `won` filter, and
   * that is a second shape rather than a violation of the rule above. Nearly
   * every confirmed customer is there BECAUSE they won — the claim writes
   * `status: 'confirmed'` in the same `$set` — so the field is meaningful for
   * the list even though a minority of rows lack it. Those sort as null, which
   * in `desc` puts every real claim above every hand-moved row: the right answer
   * rather than a tolerated one.
   *
   * What that shape does need is a tiebreak, since the null rows all compare
   * equal to each other and there are enough of them to shuffle visibly between
   * fetches. The repo adds one; see `sortFor`.
   *
   * Still not something to offer on a list where it describes nothing — ordering
   * Pending by "when they won" arranges 196 people by a field none of them has.
   */
  sort?: 'created_at' | 'updated_at' | 'name' | 'customer_id' | 'won_at' | 'active_hit_at'
  dir?: 'asc' | 'desc'
  limit?: number
  offset?: number
  /** Skip the `countDocuments` pass when paging — saves a full scan. */
  withTotal?: boolean
}

/* ------------------------------------------------------------------ *
 * Automation start / sign-in
 * ------------------------------------------------------------------ */

/** The pipeline steps the start flow reports, in order. */
export const SIGNIN_FLOW_STEPS = [
  'browser',
  'csrf',
  'sign_in',
  'otp_wait',
  'confirm_otp',
  'authorize',
  /*
   * The rehire gate: one GET asking whether Amazon will hire this customer.
   *
   * Runs on the bearer token confirm-otp has just produced — and, on the session
   * reuse path, on a rehydrated one, which is why it is a step of its own rather
   * than something folded into `confirm_otp`. A customer Amazon reports as
   * ineligible stops here, having created nothing.
   */
  'rehire_check',
  // Runs after the browser is already closed, on the warm connection the
  // sign-in left behind. Listed here because the Start flow streams it as part
  // of the same run, not because it is part of authentication.
  'create_application',
  // The confirm that actually claims the seat: create leaves the application in
  // SHIFT_SELECTION_PENDING, and this moves it to JOB_SELECTED. The 60s
  // heartbeat re-sends the same call out of band; only this first one is
  // streamed, because only this one is part of applying.
  'update_application',
] as const
export type SignInFlowStep = (typeof SIGNIN_FLOW_STEPS)[number]

/** Friendly labels for the start-flow steps. */
export const SIGNIN_STEP_LABELS: Readonly<Record<SignInFlowStep, string>> = Object.freeze({
  browser: 'Opening a secure session',
  csrf: 'Getting a session token',
  sign_in: 'Signing in',
  otp_wait: 'Waiting for the email code',
  confirm_otp: 'Confirming the code',
  authorize: 'Fetching candidate id',
  rehire_check: 'Checking rehire eligibility',
  create_application: 'Creating the application',
  update_application: 'Confirming the job and shift',
})

/**
 * The redacted result of a sign-in. No secret ever appears here — the captured
 * tokens are reported only as booleans, since the browser has no reason to hold
 * them and every reason not to.
 */
/**
 * What the application step produced.
 *
 * Not a secret — an application id is an opaque handle the operator needs in
 * order to follow the record anywhere else, so unlike the sign-in tokens it is
 * shown in full rather than as a presence flag.
 */
export interface ApplicationResultDto {
  /**
   * `null` when the run was ARMED without filing anything.
   *
   * That is the Brazil flow's ordinary Start: no application is created until a
   * shift actually opens, because on that flow's endpoint the create IS the
   * claim. The step still ran and still succeeded — it signed in, held the
   * address and armed the detectors — so this is a real result with a real
   * elapsed time, and only the application half of it is empty.
   */
  applicationId: string | null
  /** e.g. `APPLICATION_CREATED`. Null when nothing was filed. */
  currentState: string | null
  /** What the funnel wants next, e.g. `SHIFT_SELECTION_PENDING`. */
  nextState: string | null
  jobId: string
  scheduleId: string
  ms: number
}

export interface SignInResultDto {
  ok: boolean
  customerId: string
  /** Present when the application step ran and succeeded. */
  application?: ApplicationResultDto | null
  /** Present on success. */
  candidateId: string | null
  candidateSource: 'cache' | 'authorize' | null
  market: string
  /** ISP endpoint used, e.g. "isp.oxylabs.io:8006" or "direct". */
  egress: string | null
  /** Presence flags for the captured session — never the values. */
  captured: {
    authSession: boolean
    accessToken: boolean
    idToken: boolean
    refreshToken: boolean
    authorizationCode: boolean
    csrfToken: boolean
  }
  expiresAtMs: number | null
  timings: { step: SignInFlowStep; ms: number; attempts: number }[]
  totalMs: number
  /** Present on failure. */
  failedStep?: SignInFlowStep
  message?: string
  retryable?: boolean
}

/** A redacted record of one HTTP call the flow made, for the debug inspector. */
export interface SignInCallDto {
  step: SignInFlowStep
  method: string
  url: string
  requestHeaders: Record<string, string>
  requestBody: unknown
  status: number
  responseHeaders: Record<string, string>
  responseBody: unknown
  ms: number
}

/** One line of the start-flow stream (NDJSON). */
export type SignInStreamEvent =
  | { type: 'accepted'; customerId: string; at: string }
  | { type: 'step_start'; step: SignInFlowStep; at: number }
  | { type: 'step_done'; step: SignInFlowStep; ms: number; attempts: number; at: number }
  | {
      type: 'step_fail'
      step: SignInFlowStep
      ms: number
      message: string
      retryable: boolean
      /**
       * The attempt failed and the run is about to try it again by itself.
       *
       * Distinct from `retryable`, which only says the failure is of a retryable
       * KIND. This says a retry is actually happening, so a reader must not
       * render the run as finished — the sign-in step recovers a stale CSRF
       * token and a rate limit in-run, and both arrive here first as a failure.
       */
      recovering: boolean
      at: number
    }
  | { type: 'call'; call: SignInCallDto }
  | { type: 'result'; result: SignInResultDto }
  | { type: 'error'; message: string }

/* ------------------------------------------------------------------ *
 * The live run — automation this system performs
 *
 * Distinct from `SessionSummary`/`LogEntry`, which describe the LEGACY
 * `customer_sessions` + `session_logs` collections owned by the other project.
 * A run started here has no row in either, so it carries its own contract: a
 * bounded log ring keyed by customer, plus a counter block computed from
 * structured fields.
 * ------------------------------------------------------------------ */

/**
 * The moments in a run worth ANNOUNCING rather than merely recording.
 *
 * Set at the call site on the one line that marks each moment, exactly as
 * `httpStatus` and `ms` are, and for exactly the same reason: the console
 * reacts to a run's turning points without pattern-matching message text. A
 * reworded message must never silence an alert, and this file already carries
 * that warning twice.
 *
 * Deliberately tiny, and deliberately not one per `stepKey`. These are the
 * three outcomes an operator watching fifty rows wants to know about from
 * across the room, not a narration of the pipeline.
 */
export const RUN_EVENTS = ['active', 'burst', 'miss', 'won'] as const
export type RunEvent = (typeof RUN_EVENTS)[number]

/* ------------------------------------------------------------------ *
 * Fleet sounds
 *
 * One optional uploaded audio file per run event, stored server-side so every
 * console plays the same thing. The FILE travels over its own binary endpoint
 * (`GET /automation/sounds/:event/file`); these DTOs carry everything about it
 * except the bytes, which is what lets the meta list ride an SSE frame without
 * pushing megabytes at every console.
 * ------------------------------------------------------------------ */

/**
 * The ceiling on one uploaded sound, shared by the route's body limit and the
 * client's pre-flight check so the two cannot drift into a state where the
 * picker accepts a file the server refuses.
 */
export const HUNT_SOUND_MAX_BYTES = 3 * 1024 * 1024

/**
 * Everything about a stored sound except its bytes.
 *
 * `version` is the write's timestamp and does double duty: it busts the
 * browser's HTTP cache (the file URL carries it as a query) and lets a console
 * compare its decoded copy against a meta list without hashing anything.
 */
export interface HuntSoundMetaDto {
  event: RunEvent
  /** The uploaded file's name, shown in every console's picker. */
  name: string
  /** The upload's content type, echoed back when the file is served. */
  mime: string
  size: number
  version: number
}

/**
 * One line of a run.
 *
 * `httpStatus` and `ms` are the reason the counters below can be trusted. The
 * previous generation of this console derived its status-code tiles by
 * pattern-matching the message TEXT, which meant every reworded log line
 * silently zeroed a metric. These are set at the call site from the response
 * itself, so the numbers cannot drift away from what actually happened.
 */
export interface RunLogLineDto {
  /** One per Start. Groups every line of a single run. */
  runId: string
  level: LogLevel
  /** Machine key, e.g. `sign_in`. Drives the label and filtering. */
  stepKey: string | null
  /** `ok` | `failed` | `retrying` | null. */
  status: string | null
  message: string
  /** One short line of extra context. Never a credential. */
  smallDetail: string | null
  /**
   * The full, redacted response for a FAILED call — method, URL, status, body.
   *
   * Only ever set on failures, and only where there was a real response to
   * report. `smallDetail` answers "what happened" in one line; this answers
   * "what did the server actually say", which is the question an operator has
   * the moment the one-liner is not enough. Rendered as a collapsible block, so
   * it costs nothing until it is opened.
   */
  detail?: string | null
  attempt: number | null
  /** Epoch ms. */
  at: number
  /** Set only on lines that recorded an HTTP call. */
  httpStatus?: number | null
  /** Round-trip duration for that call, in ms. */
  ms?: number | null
  /**
   * WHICH endpoint this line is about, e.g. `GET /get-schedule-details`.
   *
   * Added because the step label alone was ambiguous to the point of being
   * misleading: the schedule watch and the claim are two entirely different
   * calls to two different hosts, and both were reaching the console under
   * `update application`. So a line reading "Schedule check refused (429)" sat
   * in a column that named the endpoint it was NOT about, next to a counter
   * strip that counts only the endpoint it was not about either.
   *
   * Method and path, never the full URL with its query string — the query
   * carries identifiers that are already on their own lines, and a 200-character
   * URL in a column destroys the layout it was added to clarify.
   */
  api?: string | null
  /**
   * A turning point worth announcing. Absent on ordinary lines, which is very
   * nearly all of them.
   *
   * EXACTLY ONE line carries each occurrence, chosen so the count is honest: a
   * burst that loses tags `burst` once and `miss` once however many calls it
   * took, and a win tags `won` once. Tagging a second line for the same moment
   * — the burst's own "wait over", say, or `reportBurstEnd` beside the
   * `reportMiss` it calls — would double every alert the operator hears.
   *
   * Optional because run-log lines are read back from Redis as unvalidated
   * JSON, and every line written before this field existed lacks the key.
   */
  event?: RunEvent
}

/**
 * Where a run has got to, as one word.
 *
 * Coarser than `stepKey` on purpose: the row pill has room for a state, not a
 * step, and an operator scanning fifty rows wants to know which ones need
 * attention rather than which HTTP call each is on.
 */
export const RUN_PHASES = [
  'idle',
  'starting',
  'signing_in',
  'awaiting_otp',
  'applying',
  'holding',
  'failed',
  'stopped',
] as const
export type RunPhase = (typeof RUN_PHASES)[number]

export const RUN_PHASE_LABELS: Readonly<Record<RunPhase, string>> = Object.freeze({
  idle: 'Idle',
  starting: 'Starting',
  signing_in: 'Logging in',
  awaiting_otp: 'Waiting for code',
  applying: 'Applying',
  holding: 'Holding shift',
  failed: 'Failed',
  stopped: 'Stopped',
})

/**
 * Live counters for one run.
 *
 * Every field is accumulated from structured data as the run happens — none of
 * it is recomputed by scanning the log, which is what makes this O(1) to read
 * and safe to push on every change.
 */
/**
 * What a run has done, measured on the ONE call that decides anything.
 *
 * ── Everything below counts `update-application`, and only that ─────
 * These counters used to be fed from the sign-in call inspector, which meant
 * the whole strip described csrf, sign-in and confirm-otp — three calls that
 * happen once, at the start, and never again. A run that had been holding a
 * seat for six hours still read `Calls 3`, and every 403/405/429 the heartbeat
 * or the strike met was invisible.
 *
 * `update-application` is the call that confirms the shift, the call the
 * heartbeat repeats every 60s, and the call the strike makes when a schedule
 * opens. It is the only one whose failures cost anything, so it is the one the
 * numbers describe. Sign-in is still fully visible in the pipeline and the log.
 *
 * Counted PER ATTEMPT, inside the client's retry loop — a 405 that is solved and
 * retried into a 200 is two lines here, which is the point: the recovery is the
 * thing being measured.
 */
export interface RunMetricsDto {
  /** `update-application` attempts, including ones that were retried. */
  calls: number
  /** ...of which the upstream accepted. */
  ok2xx: number
  /** The three refusals that mean different things and have different fixes. */
  s403: number
  s405: number
  s429: number
  /** Any other 4xx/5xx, plus transport failures that never got a status. */
  otherErrors: number
  /** Round-trip of the most recent attempt, whatever it returned, ms. */
  lastRttMs: number | null
  /**
   * Round-trip of the most recent ACCEPTED call, ms.
   *
   * Distinct from `lastRttMs` and more useful: a refusal is often fast (a WAF
   * block is answered at the edge in ~40ms), so a strip showing the last attempt
   * can report a healthy-looking number for a run that is failing.
   */
  lastOkRttMs: number | null
  /**
   * Mean round-trip of ACCEPTED calls, ms.
   *
   * The baseline a claim will pay: every 60s the heartbeat makes this exact call
   * on this exact connection, so its average is the best available estimate of
   * what the strike will cost when a schedule finally opens. A rising mean is
   * the endpoint degrading, visible long before anything fails.
   */
  meanRttMs: number | null
  /**
   * Milliseconds from the last WAF challenge to the 200 that followed it.
   *
   * A 405 from these hosts is not an error, it is the challenge — answering it
   * costs a CapSolver round trip, and this is what that cost actually was. It is
   * the number that says whether a challenge met during a claim would have lost
   * the shift.
   */
  waf405ToOkMs: number | null
  /**
   * Milliseconds from a worker seeing ACTIVE to the accepted claim.
   *
   * The end-to-end measurement of the entire system: detection, decision and the
   * strike's round trip, from the instant the schedule opened to the instant the
   * seat was confirmed. Null until a shift actually opens.
   */
  activeToOkMs: number | null
  /**
   * Epoch ms of the sign-in these credentials came from, or null.
   *
   * The ORIGINAL sign-in, carried forward across a reused session — so it is the
   * age of the bearer token rather than of this run. Read against
   * `HIRING_SESSION_RENEW_MS` it says how long until the session re-mints
   * itself.
   */
  signedInAtMs: number | null
  /** Keep-alive beats confirmed. */
  beats: number
  /** Steps that had to be retried. */
  retries: number
  /** Log lines at warn / error level. */
  warns: number
  errors: number
  /** Wall-clock since the run started, ms. */
  elapsedMs: number
}

/** A run's live state — what the row pill and the console header both read. */
export interface RunStateDto {
  customerId: string
  /** Null when this customer has never been run by this system. */
  runId: string | null
  phase: RunPhase
  /**
   * Whether the API still has machinery running for this customer — a hunt, a
   * heartbeat, or the onboarding wizard.
   *
   * Separate from `phase` because the two genuinely disagree, and when they do
   * this is the one that decides whether Stop is offered. See `RunLiveHint.live`
   * for the three ways they come apart.
   */
  live: boolean
  /** The step currently in flight, or the last one to complete. */
  stepKey: string | null
  /** Short human line under the pill, e.g. `waf-token`. */
  detail: string | null
  /** Epoch ms the run began. */
  startedAt: number | null
  /** Epoch ms of the most recent line. */
  updatedAt: number
  metrics: RunMetricsDto
  /** Set once the application exists — the handle for everything downstream. */
  applicationId: string | null
  /** e.g. `JOB_SELECTED`. */
  applicationState: string | null
  /** ISP endpoint this run is pinned to, e.g. `isp.oxylabs.io:8004`. */
  egress: string | null
  /**
   * The portal login this run signs in as.
   *
   * Carried on the state rather than looked up in the console, because the two
   * can disagree: the run authenticates as whatever was on the customer record
   * when it STARTED, and a row edited mid-run would otherwise show an address
   * the session never used. This is the one the OTP was actually sent to, which
   * is the question being asked when a run is stuck at `otp_wait`.
   */
  email: string | null
  /**
   * The customer's name, from their record.
   *
   * Read from the row at Start rather than derived from the session, because it
   * is the one identifier here that is about the PERSON rather than about the
   * run — and the operator reading a drawer full of ids and endpoints needs one
   * thing in it they recognise.
   */
  name: string | null
  /**
   * The browser identity pinned to this session, e.g. `Chrome 150 · Windows`.
   *
   * Short and human, not the raw User-Agent: the full string is 120 characters
   * of boilerplate around the two facts worth seeing. It is pinned for the
   * session's whole life alongside the egress — the WAF watched this exact pair
   * mint the token — so showing them together makes a mismatch visible.
   */
  agent: string | null
  /**
   * The job and shift this RUN chose for itself, because the customer had
   * neither assigned. Null on every ordinary run.
   *
   * ── Why it lives on the run and not on the customer row ─────────────
   * The pair is never written back to Mongo, so `CustomerRow` cannot carry it:
   * the change-stream watcher republishes `job_id` / `schedule_id` from the
   * document, and every such frame would re-assert `null` over the flag. Here
   * it belongs to the thing that actually chose it and dies with it, which is
   * also the honest lifetime — a restart must re-decide rather than inherit a
   * pick that only ever existed in one process's memory.
   *
   * Optional because run states are read back from Redis as unvalidated JSON,
   * and every snapshot written before this field existed lacks the key.
   */
  autoTarget?: {
    /** Qualified, e.g. `JOB-CA-0000000591`. */
    jobId: string
    /** Qualified, e.g. `SCH-CA-0000006274`. */
    scheduleId: string
    /** Why it matched — the site the customer accepts, e.g. `SITE-YYZ1`. */
    siteId: string
    /** The other half of why it matched. */
    scheduleType: ScheduleType
    /**
     * How many seats the shift has open — its `laborDemandAvailableCount` — as
     * the schedule workers last read it.
     *
     * Set from the very snapshot that justified the pick (so it is always
     * positive at the moment the chip first appears; a shift with no room is
     * never picked) and then kept current from the hunt's own reads of the
     * schedule for as long as the run is tracked, so the figure beside the AUTO
     * chip is the live one, not the one from the moment of choosing. The Mongo
     * count is never read again after the pick.
     *
     *   a number    what the schedule reported on the newest read that carried
     *               a count — 0 means the queue has filled since the pick
     *   `null`      no count has been read for this shift
     *   absent      a snapshot written before this field existed
     *
     * The three are distinct on purpose: `0` is a fact about the shift, `null`
     * is the absence of one, and a legacy frame must render nothing rather than
     * pretend to either.
     */
    seats?: number | null
    /**
     * When `seats` was read: the row's own `updatedAtMs` for the pick-time
     * figure, the read's clock once the schedule workers take over — so an
     * operator can tell a fresh 0 from a stale one. Null whenever `seats` is.
     */
    seatsAtMs?: number | null
  } | null
}

/* ------------------------------------------------------------------ *
 * System metrics
 *
 * Two sources, deliberately kept apart rather than blended into one number.
 *
 *   Railway  — the container's own accounting for EVERY service in the
 *              project, including the ones this process cannot introspect
 *              (Redis, the static web image). Authoritative, and the only
 *              place the plan limits come from. Lands on a 30s grid and runs
 *              20–60s behind wall clock (both ends measured), because that is
 *              Railway's ingestion floor rather than anything tunable here.
 *   Self     — `process.cpuUsage()` and friends, read inside this process.
 *              One service only, but it is the service that matters and it
 *              is CURRENT: no ingestion, no polling upstream, no lag.
 *
 * Merging them would produce a chart whose points mean different things at
 * different x positions, so the console shows each for what it is and only
 * borrows Railway's LIMITS to scale the self-reported gauges.
 * ------------------------------------------------------------------ */

/** One sample. `ts` is epoch SECONDS — Railway's unit, kept unconverted. */
export interface MetricPoint {
  ts: number
  value: number
}

export interface ServiceMetricsDto {
  serviceId: string
  /** Railway's service name, e.g. `@acs/api`. */
  name: string
  /** vCPU consumed. Compare against `cpuLimit`, not against 1. */
  cpu: MetricPoint[]
  memoryGb: MetricPoint[]
  networkRxGb: MetricPoint[]
  networkTxGb: MetricPoint[]
  /**
   * Plan ceilings. Null when Railway reports no limit series for the service
   * — a volume-less service has no disk limit, and a brand new one has no
   * samples at all yet. A null must render as "—", never as 0, or an idle
   * service reads as one that is wedged at 100%.
   */
  cpuLimit: number | null
  memoryLimitGb: number | null
}

export interface SystemMetricsDto {
  /**
   * False when no `RAILWAY_API_TOKEN` is set, which is the ordinary state of a
   * laptop and the initial state of the deploy. The console then shows the
   * self-reported half and says why the rest is missing, rather than showing
   * an error over a feature that is merely switched off.
   */
  configured: boolean
  projectId: string | null
  environmentId: string | null
  services: ServiceMetricsDto[]
  /** Epoch ms this snapshot was fetched. Not the age of its newest sample. */
  fetchedAt: number
  /** Grid the points sit on. 30 is Railway's floor; smaller is rejected. */
  sampleRateSeconds: number
  /**
   * Why the last upstream call failed, or null.
   *
   * Carried in the body rather than raised as a 5xx: a stale snapshot plus a
   * reason is strictly more useful to an operator than an error page, and the
   * self-reported half of this payload is still perfectly good.
   */
  error: string | null
}

/**
 * What this API process says about itself, sampled in-process.
 *
 * Pushed rather than polled, because the entire reason it exists is to not be
 * 45 seconds behind.
 */
export interface SelfMetricsDto {
  /** The `INSTANCE_ID` of the replica that produced it. */
  instanceId: string
  /** Epoch ms at the moment of sampling. */
  at: number
  /**
   * vCPU used over the interval since the previous sample — so `1.0` is one
   * core saturated, and on a multi-core box it can legitimately exceed 1.
   * Directly comparable with Railway's `CPU_USAGE`, which is the same unit.
   */
  cpu: number
  rssGb: number
  heapUsedGb: number
  heapTotalGb: number
  uptimeMs: number
  /** SSE clients on THIS replica, matching `/api/health`'s `streams`. */
  streams: number
  /**
   * Worst event-loop delay observed since the previous sample, in ms.
   *
   * The one number here that a memory graph cannot tell you. Every ceiling
   * this process has is single-threaded — see the sign-in admission limit —
   * so saturation shows up as a loop that stops turning long before RSS moves,
   * and a box at 4% CPU with a 900ms loop is a box that is fully busy.
   */
  eventLoopDelayMs: number
}

/** `GET /api/system/metrics` — both halves in one read, so a cold panel paints once. */
export interface SystemSnapshotDto {
  railway: SystemMetricsDto
  self: SelfMetricsDto
  /**
   * Which entry in `railway.services` is the process that answered, when the
   * deployment told it. Lets the console mark one card "this API" and scale its
   * self-reported gauges against that service's plan limits.
   */
  selfServiceId: string | null
}

/* ------------------------------------------------------------------ *
 * Resume roster — restarting a fleet after a deploy
 * ------------------------------------------------------------------ */

/**
 * Which saved list a resume reads.
 *
 * `manual` is the operator's own selection, written by an explicit Save.
 * `auto` is the server's snapshot of whatever was running — refreshed
 * periodically and again at shutdown — so a deploy nobody prepared for still
 * leaves a list behind.
 */
export const RESUME_SOURCES = ['manual', 'auto'] as const
export type ResumeSource = (typeof RESUME_SOURCES)[number]

export interface ResumeRosterDto {
  source: ResumeSource
  /**
   * `customers.customer_id` values ("mdesai000356") — never row `_id`s. The
   * customer id is the one key that survives a redeploy, a re-import and a
   * table refresh, and it is what every operator-facing surface prints.
   */
  customerIds: string[]
  /** Epoch ms the roster was written. */
  savedAt: number
  /** The operator who saved it, or `'periodic'` / `'shutdown'` for the auto roster. */
  savedBy: string
}

/**
 * What became of one roster member when the server reached it.
 *
 * `skipped_*` is not failure: `skipped_running` means the ladder found the
 * customer already doing something (parked watch, heartbeat, wizard, sign-in
 * in flight) and correctly left it alone; `skipped_gender` is the one refusal
 * an unattended runner cannot fix, because fixing it is a dialog.
 */
export const RESUME_OUTCOMES = [
  'started',
  'skipped_running',
  'skipped_gender',
  'skipped_missing',
  'failed',
] as const
export type ResumeOutcome = (typeof RESUME_OUTCOMES)[number]

export interface ResumeItemDto {
  customerId: string
  outcome: ResumeOutcome
  /** The server's refusal or exception sentence; null when the start was accepted. */
  message: string | null
  /** Epoch ms. */
  at: number
}

/**
 * How many exceptions one run reports individually.
 *
 * Past this the tallies still count every customer — only the per-customer
 * list stops growing. The cap exists because the realistic worst case is a
 * whole roster skipped at once (a resume pressed while the fleet is already
 * running), and re-sending five hundred identical "already running" lines
 * several times a second for the length of the run is a lot of bytes to say
 * one thing that `counts` already says exactly.
 */
export const RESUME_ITEMS_MAX = 100

export type ResumeRunPhase = 'idle' | 'running' | 'cancelling' | 'done' | 'cancelled'

/**
 * The resume runner, whole. Every frame is a complete snapshot — newest wins,
 * nothing merges — the same replace-don't-reduce rule `run.state` follows.
 */
export interface ResumeStateDto {
  phase: ResumeRunPhase
  /** Null until the first resume of this process's life. */
  runId: string | null
  source: ResumeSource | null
  total: number
  /** Customers processed so far, whatever the outcome. */
  done: number
  /** The customer whose start is in flight right now, or null. */
  current: string | null
  startedAt: number | null
  finishedAt: number | null
  /** The stagger the loop is honouring right now — re-read live from hunt settings. */
  staggerMs: number
  /**
   * The EXCEPTIONS — every outcome that was not a plain `started`, capped at
   * `RESUME_ITEMS_MAX`.
   *
   * Deliberately not every customer. A started customer is already reported by
   * its own `run.state` frames and lights up its own row, so listing it here
   * would put the same fact on the wire twice, several times a second, for the
   * whole run. What an operator cannot see anywhere else is what did NOT
   * start, and that is what this carries. `counts` remains the full tally.
   */
  items: ResumeItemDto[]
  /** The complete tally — exact for every outcome, whatever `items` had room for. */
  counts: Record<ResumeOutcome, number>
  /** Exceptions that happened past the `items` cap, so the UI can say "+N more". */
  itemsOmitted: number
}

/** `GET /api/automation/resume` — both rosters and the runner in one read. */
export interface ResumeSnapshotDto {
  manual: ResumeRosterDto | null
  auto: ResumeRosterDto | null
  run: ResumeStateDto
  /**
   * How many customers "Save all running" would capture, counted by the SERVER
   * with the very function that performs the save.
   *
   * On the wire rather than derived in the browser, and that is the whole
   * point. Every client-side approximation of this number is a different
   * expression over a different snapshot — the run map is seeded once and
   * carries a `live` flag frozen at publish time, and `stats.live` is a fourth
   * thing again, counted across every status. Each drifts from the save by a
   * customer or two in whichever direction the staleness happens to fall, which
   * is exactly what a button that names a count must never do.
   *
   * So the promisor states the number. `currentlyRunningIds().length` here and
   * `currentlyRunningIds()` at save time are the same call in the same process,
   * so the label can only be wrong by however long this snapshot has been in
   * the client's cache.
   */
  runningNow: number
  at: number
}

/* ------------------------------------------------------------------ *
 * Realtime (SSE)
 * ------------------------------------------------------------------ */

export type ServerEvent =
  | { type: 'hello'; at: string; streamId: string }
  | { type: 'heartbeat'; at: string }
  | { type: 'customer.patch'; id: string; patch: Partial<CustomerRow>; at: string }
  /*
   * `session.patch` and `log.append` are gone.
   *
   * Both were produced by change streams over `customer_sessions` and
   * `session_logs`, which the SIBLING fleet writes. `log.append` was also the
   * single noisiest frame on the bus: one per inserted document, and the bus
   * has no per-subscriber filter, so every console received every one of the
   * other project's log lines in order to throw them away.
   */
  | { type: 'stats'; stats: ConsoleStats }
  /**
   * A batch of run-log lines, coalesced server-side.
   *
   * Batched rather than one frame per line because a sign-in emits lines in
   * bursts — a step completing writes its own line plus one per HTTP call it
   * made — and a frame per line would be a frame per few milliseconds for a
   * console that repaints once per animation frame anyway.
   */
  | { type: 'run.append'; customerId: string; lines: RunLogLineDto[] }
  /** The run's live state. Sent whenever the phase or a counter changes. */
  | { type: 'run.state'; state: RunStateDto }
  /** The resume runner's progress. A complete snapshot; newest wins. */
  | { type: 'resume.state'; state: ResumeStateDto }
  /**
   * The fleet's uploaded sounds changed. Carries the COMPLETE meta list — a
   * snapshot, not a delta — so a console can reconcile in one pass (fetching
   * only files whose version moved) and a missed frame costs nothing that the
   * next one does not repair.
   */
  | { type: 'sounds.changed'; sounds: HuntSoundMetaDto[] }
  /**
   * This replica's own vitals, on a fixed tick.
   *
   * Delivered LOCALLY ONLY — see `publishLocal` in the bus. It describes the
   * process the client is attached to, so relaying it to the other replicas
   * would have each console showing a random peer's numbers as if they were
   * the numbers of the process serving it.
   */
  | { type: 'system.self'; self: SelfMetricsDto }

export const SSE_HEARTBEAT_MS = 15_000

/**
 * How often this process samples and pushes its own vitals.
 *
 * Shared so the browser can size its rolling window in samples rather than
 * guessing at a duration, and so the two cannot drift apart.
 */
export const SELF_METRICS_INTERVAL_MS = 2_000

/** Railway's minimum. 5/10/15 are rejected by its API outright. */
export const RAILWAY_SAMPLE_RATE_SECONDS = 30
